import { Box, Button, CircularProgress, Dialog, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { motion, AnimatePresence } from "framer-motion";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
} from "../../../../pages/Home/constants/homeGlass";
import {
  MODAL_MAX_HEIGHT_XS,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
} from "../../../layoutMetrics";
import {
  MODAL_HEIGHT,
  MODAL_LEFT_INSET,
  MODAL_RIGHT_MARGIN,
  MODAL_VERTICAL_INSET_SM,
  MODAL_WIDTH,
  stepContentCardSx,
  stepMotionVariants,
  THEME_ACCENT,
  DARK_BORDER,
  WIZARD_STEPS,
} from "./shared";
import useCreateClientModal from "./hooks/useCreateClientModal";
import ClientDetailsStep from "./ClientDetailsStep";
import ClientAssetsStep from "./ClientAssetsStep";
import ClientUsersStep from "./ClientUsersStep";
import ClientThemeStep from "./ClientThemeStep";
import SaveProgressIndicator from "../SaveProgressIndicator";

export default function CreateClientModal({
  open,
  onClose,
  onSubmit,
  loading = false,
  saveProgress = null,
  mode = "create",
  editTarget = null,
  defaultStep = null,
}) {
  const {
    isEditMode,
    isAdmin,
    creatorUser,
    form,
    setForm,
    error,
    domains,
    domainsLoading,
    activeStep,
    allUsers,
    usersLoading,
    clientRoles,
    clientRolesLoading,
    userAssignments,
    existingResources,
    existingResourcesLoading,
    deletingThemeNames,
    deletingResourceKeys,
    downloadingResourceKeys,
    contentRef,
    canGoPrevious,
    canGoNext,
    canSubmit,
    handleNextStep,
    handlePreviousStep,
    handleStepClick,
    handleDialogClose,
    handleSelectedUsersChange,
    handleRemoveUser,
    handleUpdateUserRoles,
    handleRemoveExistingResource,
    handleDownloadExistingResource,
    handleRemoveExistingTheme,
    handleRemoveAllExistingThemes,
    handleSubmit,
  } = useCreateClientModal({
    open,
    onClose,
    onSubmit,
    mode,
    editTarget,
    defaultStep,
  });

  const currentStepMeta = WIZARD_STEPS[activeStep];

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "flex-start",
          alignItems: { xs: "center", sm: "stretch" },
          paddingLeft: { xs: 1, sm: MODAL_LEFT_INSET },
          paddingRight: { xs: 1, sm: MODAL_RIGHT_MARGIN },
          paddingTop: {
            xs: `max(8px, ${SAFE_AREA_TOP})`,
            sm: MODAL_VERTICAL_INSET_SM,
          },
          paddingBottom: {
            xs: `max(8px, ${SAFE_AREA_BOTTOM})`,
            sm: MODAL_VERTICAL_INSET_SM,
          },
          boxSizing: "border-box",
        },
      }}
      PaperProps={{
        sx: {
          m: 0,
          width: {
            xs: "calc(100vw - 16px)",
            sm: MODAL_WIDTH,
          },
          height: {
            xs: "auto",
            sm: MODAL_HEIGHT,
          },
          maxWidth: {
            xs: "min(100%, calc(100vw - 16px))",
            sm: MODAL_WIDTH,
          },
          maxHeight: {
            xs: MODAL_MAX_HEIGHT_XS,
            sm: MODAL_HEIGHT,
          },
          borderRadius: "16px",
          overflow: "hidden",
          background:
            "linear-gradient(145deg, rgba(64, 64, 53, 0.78), rgba(28, 29, 22, 0.85)), radial-gradient(circle at 0% 0%, rgba(219, 165, 41, 0.12), transparent 50%)",
          backdropFilter: HOME_GLASS_BACKDROP,
          WebkitBackdropFilter: HOME_GLASS_BACKDROP,
          border: HOME_GLASS_BORDER,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          color: "#fff",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2.25,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                background: `${THEME_ACCENT}22`,
                border: `1px solid ${THEME_ACCENT}44`,
              }}
            >
              <BusinessOutlinedIcon sx={{ color: THEME_ACCENT, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em" }}>
                {isEditMode ? "Edit Client" : "Create New Client"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.45)", mt: 0.2 }}>
                {`Step ${activeStep + 1} of ${WIZARD_STEPS.length} · ${currentStepMeta.label}`}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            sx={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              border: `1px solid ${DARK_BORDER}`,
              color: "rgba(255,255,255,0.5)",
              flexShrink: 0,
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.06)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.25)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Wizard stepper */}
        <Box
          sx={{
            mb: 2.5,
            flexShrink: 0,
            px: { xs: 0, sm: 0.5 },
          }}
          role="navigation"
          aria-label="Create client wizard steps"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            {WIZARD_STEPS.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              const isLast = index === WIZARD_STEPS.length - 1;
              const Icon = step.icon;
              const canNavigate = typeof handleStepClick === "function";

              return (
                <Box
                  key={step.id}
                  sx={{
                    flex: isLast ? "0 0 auto" : 1,
                    display: "flex",
                    alignItems: "flex-start",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => canNavigate && handleStepClick(index)}
                    disabled={!canNavigate}
                    aria-current={isActive ? "step" : undefined}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.75,
                      border: "none",
                      background: "none",
                      cursor: canNavigate ? "pointer" : "default",
                      p: 0,
                      flexShrink: 0,
                      minWidth: { xs: 52, sm: 72 },
                      "&:disabled": { cursor: "default" },
                      "&:focus-visible": {
                        outline: `2px solid ${THEME_ACCENT}`,
                        outlineOffset: 4,
                        borderRadius: "8px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        transition: "all 250ms ease",
                        background: isActive
                          ? `${THEME_ACCENT}22`
                          : isCompleted
                            ? `${THEME_ACCENT}14`
                            : "rgba(255,255,255,0.05)",
                        border: isActive
                          ? `2px solid ${THEME_ACCENT}`
                          : isCompleted
                            ? `1.5px solid ${THEME_ACCENT}88`
                            : "1.5px solid rgba(255,255,255,0.12)",
                        boxShadow: isActive ? `0 0 18px ${THEME_ACCENT}33` : "none",
                      }}
                    >
                      {isCompleted ? (
                        <CheckIcon sx={{ fontSize: 18, color: THEME_ACCENT }} />
                      ) : (
                        <Icon
                          sx={{
                            fontSize: 17,
                            color: isActive ? THEME_ACCENT : "rgba(255,255,255,0.35)",
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: { xs: 10, sm: 11.5 },
                        fontWeight: isActive ? 700 : 500,
                        color: isActive
                          ? "#fff"
                          : isCompleted
                            ? THEME_ACCENT
                            : "rgba(255,255,255,0.4)",
                        textAlign: "center",
                        lineHeight: 1.25,
                        letterSpacing: "0.01em",
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        {step.label}
                      </Box>
                      <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
                        {step.shortLabel}
                      </Box>
                    </Typography>
                  </Box>

                  {!isLast ? (
                    <Box
                      sx={{
                        flex: 1,
                        height: 2,
                        mx: { xs: 0.25, sm: 0.75 },
                        mt: "18px",
                        borderRadius: 1,
                        minWidth: 8,
                        background: isCompleted
                          ? `linear-gradient(90deg, ${THEME_ACCENT}, ${THEME_ACCENT}66)`
                          : "rgba(255,255,255,0.08)",
                        transition: "background 300ms ease",
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <Box
              key={activeStep}
              component={motion.div}
              variants={stepMotionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22, ease: "easeOut" }}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box ref={contentRef} sx={stepContentCardSx}>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    px: { xs: 2, sm: 2.75 },
                    py: { xs: 2, sm: 2.5 },
                    "&::-webkit-scrollbar": { width: 5 },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "rgba(255,255,255,0.12)",
                      borderRadius: 999,
                    },
                  }}
                >
                  {activeStep === 0 ? (
                    <ClientDetailsStep
                      form={form}
                      setForm={setForm}
                      domains={domains}
                      domainsLoading={domainsLoading}
                      open={open}
                      activeStep={activeStep}
                    />
                  ) : null}

                  {activeStep === 1 ? (
                    <ClientAssetsStep
                      form={form}
                      setForm={setForm}
                      existingResources={
                        isEditMode
                          ? {
                              knowledgeBank: existingResources.knowledgeBank,
                              logo: existingResources.logo,
                              meeting: existingResources.meeting,
                              otherDocuments: existingResources.otherDocuments,
                            }
                          : undefined
                      }
                      existingResourcesLoading={
                        isEditMode ? existingResourcesLoading : false
                      }
                      deletingResourceKeys={
                        isEditMode ? deletingResourceKeys : []
                      }
                      downloadingResourceKeys={
                        isEditMode ? downloadingResourceKeys : []
                      }
                      onRemoveExistingResource={
                        isEditMode ? handleRemoveExistingResource : undefined
                      }
                      onDownloadExistingResource={
                        isEditMode ? handleDownloadExistingResource : undefined
                      }
                    />
                  ) : null}

                  {activeStep === 2 ? (
                    <ClientUsersStep
                      allUsers={allUsers}
                      usersLoading={usersLoading}
                      clientRoles={clientRoles}
                      clientRolesLoading={clientRolesLoading}
                      userAssignments={userAssignments}
                      creatorUser={creatorUser}
                      isAdmin={isAdmin}
                      onSelectedUsersChange={handleSelectedUsersChange}
                      onRemoveUser={handleRemoveUser}
                      onUpdateRoles={handleUpdateUserRoles}
                    />
                  ) : null}

                  {activeStep === 3 ? (
                    <ClientThemeStep
                      form={form}
                      setForm={setForm}
                      isEditMode={isEditMode}
                      existingThemes={isEditMode ? existingResources.theme : []}
                      existingThemesLoading={
                        isEditMode ? existingResourcesLoading : false
                      }
                      deletingThemeNames={deletingThemeNames}
                      onRemoveExistingTheme={
                        isEditMode ? handleRemoveExistingTheme : undefined
                      }
                      onRemoveAllExistingThemes={
                        isEditMode ? handleRemoveAllExistingThemes : undefined
                      }
                    />
                  ) : null}
                </Box>
              </Box>
            </Box>
          </AnimatePresence>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            flexShrink: 0,
            pt: 2,
            mt: 2,
            borderTop: `1px solid ${DARK_BORDER}`,
            pb: { xs: `max(4px, ${SAFE_AREA_BOTTOM})`, sm: 0 },
          }}
        >
          <SaveProgressIndicator
            active={loading}
            progress={saveProgress}
            accentColor={THEME_ACCENT}
          />

          <Box
            sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              color: "rgba(255,255,255,0.4)",
              fontWeight: 500,
            }}
          >
            Step {activeStep + 1} of {WIZARD_STEPS.length}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.25,
              flexWrap: "wrap",
              ml: "auto",
            }}
          >
            {error ? (
              <Typography sx={{ fontSize: 12.5, color: "#ff8b8b", mr: "auto" }}>
                {error}
              </Typography>
            ) : null}

            {canGoPrevious ? (
              <Button
                onClick={handlePreviousStep}
                disabled={loading}
                startIcon={<ArrowLeftIcon sx={{ fontSize: 18 }} />}
                sx={{
                  minWidth: 96,
                  py: 0.9,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  color: "#cbd5e1",
                  border: "1px solid rgba(255,255,255,0.28)",
                  "&:hover": { background: "rgba(255,255,255,0.08)" },
                }}
              >
                Back
              </Button>
            ) : null}

            {canGoNext ? (
              <Button
                onClick={handleNextStep}
                disabled={loading}
                endIcon={<ArrowRightIcon sx={{ fontSize: 18 }} />}
                sx={{
                  minWidth: 120,
                  py: 0.9,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  color: "#cbd5e1",
                  border: "1px solid rgba(255,255,255,0.28)",
                  "&:hover": { background: "rgba(255,255,255,0.08)" },
                }}
              >
                Continue
              </Button>
            ) : null}

            <Button
              onClick={handleSubmit}
              disabled={loading || !canSubmit}
              startIcon={
                loading ? (
                  <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} />
                ) : (
                  <PersonAddOutlinedIcon />
                )
              }
              sx={{
                minWidth: 140,
                py: 0.9,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                background: canSubmit && !loading ? THEME_ACCENT : "rgba(255,255,255,0.08)",
                color: canSubmit && !loading ? "#111" : "rgba(255,255,255,0.35)",
                boxShadow: canSubmit && !loading ? `0 4px 20px ${THEME_ACCENT}44` : "none",
                "&:hover": canSubmit && !loading
                  ? {
                      background: THEME_ACCENT,
                      filter: "brightness(1.08)",
                      boxShadow: `0 6px 22px ${THEME_ACCENT}55`,
                    }
                  : {},
                "&.Mui-disabled": {
                  background: "rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.3)",
                  boxShadow: "none",
                },
              }}
            >
              {loading
                ? isEditMode
                  ? "Saving..."
                  : "Compiling knowledgeBank..."
                : isEditMode
                  ? "Save Changes"
                  : "Create Client"}
            </Button>
          </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
